'use client';

import { SignInDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useMediaQuery } from '@eliadmin/core/src/hooks/use-media-query';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import { redirect } from 'next/navigation';
import { useState } from 'react';
import { FaTruck, FaUser } from 'react-icons/fa';
import { FiLock, FiLogOut, FiUser } from 'react-icons/fi';

type HeaderProfileButtonProps = {
  className?: {
    profile?: string;
  };
  order?: number;
};

export const HeaderProfileButton = ({ className, order }: HeaderProfileButtonProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const { t } = useTranslations();

  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);

  const openDialog = useDialogStore(s => s.open);

  const warmSocialAuthProviders = () => {
    void import('@eliadmin/core/src/components/auth/social-auth-buttons')
      .then(module => module.prefetchSocialAuthProviders(locale))
      .catch(() => undefined);
  };

  const handleSignInClick = () => {
    warmSocialAuthProviders();
    openDialog({
      id: SignInDialog.id,
      content: <SignInDialog />,
    });
  };

  const supportsHover = useMediaQuery('(hover: hover)');

  return (
    <div className="relative" style={{ order: order }}>
      {me ? (
        <Link
          href={ROUTES.PROFILE() ?? '#'}
          className={cn('ea-header-main__profile color-primary', className?.profile)}
          prefetch={false}
          onMouseEnter={() => supportsHover && setIsHovered(true)}
          onMouseLeave={() => supportsHover && setIsHovered(false)}
        >
          <Button
            variant={HEADER_CONFIG.headerMain.profile.buttonVariant}
            size="icon-lg"
            className={cn('w-auto px-3', isHovered && 'text-primary', 'hover:text-primary')}
          >
            <FaUser className="mr-1 h-6 w-6" />
            <p>{me.customer.name}</p>
          </Button>
        </Link>
      ) : (
        <Button
          aria-label={t('text190')}
          variant={HEADER_CONFIG.headerMain.profile.buttonVariant}
          size="icon-lg"
          onClick={() => handleSignInClick()}
          onMouseEnter={() => supportsHover && setIsHovered(true)}
          onMouseLeave={() => supportsHover && setIsHovered(false)}
        >
          {HEADER_CONFIG.headerMain.profile.icon}
        </Button>
      )}
      {isHovered && !me && (
        <div
          className="absolute right-0 top-[40px] mt-2 w-32 rounded-xl bg-white shadow-xl"
          style={{ zIndex: 1000 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex flex-col p-1">
            <Button
              variant="outline"
              size="sm"
              className="mb-1 min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              onClick={() => handleSignInClick()}
            >
              <FaUser className="mr-2 h-6 w-6 text-primary" />
              {t('text190')}
            </Button>
            <Link
              href={ROUTES.REGISTER() ?? '#'}
              style={{ order: order }}
              className="min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              prefetch={false}
            >
              <Button
                variant="outline"
                size="sm"
                className="w-full min-w-0 border-none p-0 font-semibold shadow-none outline-none"
              >
                <FiLock className="mr-2 h-6 w-6 text-primary" />
                {t('text261')}
              </Button>
            </Link>
          </div>
        </div>
      )}
      {isHovered && me && (
        <div
          className="absolute right-[-56px] top-[40px] mt-2 w-56 rounded-xl bg-white px-1 shadow-xl"
          style={{ zIndex: 1000 }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className="flex flex-col  p-1">
            <Link
              href={ROUTES.PROFILE() ?? '#'}
              className=" flex min-w-0 justify-start border-none p-0 font-semibold shadow-none outline-none"
              prefetch={false}
            >
              <Button
                variant="outline"
                size="sm"
                className="flex w-full min-w-0 justify-start border-none p-0 px-6 font-semibold shadow-none outline-none"
              >
                <FaUser className="mr-2 h-6 w-6 text-primary" />
                {t('text1134')}
              </Button>
            </Link>
            <Link
              href={ROUTES.ORDERS() ?? '#'}
              className=" flex min-w-0 justify-start border-none p-0 font-semibold shadow-none outline-none"
              prefetch={false}
            >
              <Button
                variant="outline"
                size="sm"
                className="flex w-full min-w-0 justify-start border-none p-0 px-6 font-semibold shadow-none outline-none"
              >
                <FaTruck className="mr-2 h-6 w-6 text-primary" />
                {t('text257')}
              </Button>
            </Link>
            <Separator className="my-1" />
            <a
              className="mb-1 flex min-w-0 justify-start border-none p-0 font-semibold shadow-none outline-none"
              href={ROUTES.SIGN_OUT()}
            >
              <Button
                variant="outline"
                size="sm"
                className="flex w-full min-w-0 justify-start border-none p-0 px-6 font-semibold shadow-none outline-none"
              >
                <FiLogOut className="mr-2 h-6 w-6 text-destructive-foreground" />
                {t('text258')}
              </Button>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};
